import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';
import Logo from './Logo';
import EditProfileModal from './EditProfileModal';
import './Navigation.css';

const Navigation = ({ title, showBackButton = false, onBackClick, onMobileMenuToggle }) => {
  const { currentUser, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Check if we're on a salon-specific page
  const isOnSalonPage = location.pathname.startsWith('/salon/');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleOpenEditProfile = () => {
    setIsDropdownOpen(false);
    setIsEditProfileModalOpen(true);
  };

  return (
    <>
    <nav className={`dashboard-nav ${isScrolled ? 'scrolled' : ''}`}>
      {onMobileMenuToggle && (
        <button className="mobile-menu-toggle" onClick={onMobileMenuToggle}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
      )}
      <div className="nav-brand">
        {title ? (
          <div className="nav-brand-logo">
            <h1>{title}</h1>
          </div>
        ) : (
          <Logo />
        )}
      </div>
      <div className="nav-actions">
        <LanguageSelector />
        <div className="nav-user" ref={dropdownRef}>
        <div className="user-dropdown">
          <button className="user-dropdown-toggle" onClick={toggleDropdown}>
            <div className="user-avatar">
              {currentUser?.firstName?.charAt(0) || currentUser?.email?.charAt(0)}
            </div>
            <span className="user-name">{currentUser?.firstName || currentUser?.email}</span>
            <svg className="dropdown-arrow" width="12" height="12" viewBox="0 0 12 12">
              <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          {isDropdownOpen && (
            <div className="user-dropdown-menu">
              <div className="dropdown-header">
                <div className="dropdown-user-info">
                  <div className="dropdown-avatar">
                    {currentUser?.firstName?.charAt(0) || currentUser?.email?.charAt(0)}
                  </div>
                  <div className="dropdown-user-details">
                    <div className="dropdown-user-name-row">
                      <div className="dropdown-user-name">{currentUser?.firstName} {currentUser?.lastName}</div>
                      <button
                        type="button"
                        className="dropdown-name-edit-btn"
                        onClick={handleOpenEditProfile}
                        title={t('navigation.editProfile')}
                        aria-label={t('navigation.editProfile')}
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M11 2l3 3-8 8-3.5 1 1-3.5 8-8z" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    <div className="dropdown-user-email">{currentUser?.email}</div>
                  </div>
                </div>
              </div>

              <div className="dropdown-divider"></div>

              <div className="dropdown-account-info">
                <div className="dropdown-info-item">
                  <label>{t('navigation.memberSince')}</label>
                  <span>
                    {currentUser?.created_at 
                      ? (() => {
                          const date = new Date(currentUser.created_at);
                          return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
                        })()
                      : 'N/A'
                    }
                  </span>
                </div>
              </div>
              
              <div className="dropdown-divider"></div>
              
              {isOnSalonPage && (
                <>
                  <button className="dropdown-back" onClick={handleBackToDashboard}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 1v6M3 3h10a2 2 0 012 2v6a2 2 0 01-2 2H3a2 2 0 01-2-2V5a2 2 0 012-2z"/>
                    </svg>
                    {t('navigation.backToDashboard')}
                  </button>
                  <div className="dropdown-divider"></div>
                </>
              )}
              
              <button className="dropdown-logout" onClick={handleLogout}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 1v6M3 3h10a2 2 0 012 2v6a2 2 0 01-2 2H3a2 2 0 01-2-2V5a2 2 0 012-2z"/>
                </svg>
                {t('navigation.logout')}
              </button>
            </div>
          )}
        </div>
        </div>
      </div>
    </nav>

    <EditProfileModal
      isOpen={isEditProfileModalOpen}
      onClose={() => setIsEditProfileModalOpen(false)}
    />
    </>
  );
};

export default Navigation;
